import { LocationMarkerIcon } from "@heroicons/react/outline";
import { get, head, isEmpty, values } from "lodash";
import { stringifyUrl } from "query-string";
import Tooltip from "rc-tooltip";
import React, { useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import InfoIcon from "../../../components/icons/InfoIcon";
import DisplayLabels from "../../../components/labels/DisplayLabels";
import { useHeliumData } from "../../../context/heliumData";
import { useHotspots } from "../../../context/hotspots";
import { useLocations } from "../../../context/locations";
import { useTooCloseToWitness } from "../../../context/tooCloseToWitness";
import { TooCloseToWitnessData } from "../../../context/tooCloseToWitness/TooCloseToWitnessProvider";
import { avatarsApi } from "../../../utils/constants";
import { formatHotspotName } from "../../../utils/hotspotUtils";
import { Hotspot, HotspotLocation } from "../../../utils/types";
import useMapConfig from "../../../utils/useMapConfig";

interface Props {
  className?: string;
  id: string; // locationId or hotspotId
  h3Index: string;
}

const NeighborsTooCloseToWitness: React.FC<Props> = ({
  className = "",
  id,
  h3Index
}) => {
  const { chainVars } = useHeliumData();
  const { hotspotsMap, getHotspotCoordinates } = useHotspots();
  const { tooCloseToWitnessData } = useTooCloseToWitness();
  const { locations, getLocationCoordinates } = useLocations();
  const renderDistance = useMapConfig((state) => state.renderDistance);

  const { hotspotsTooCloseToWitness, locationsTooCloseToWitness } = useMemo(
    () =>
      get(tooCloseToWitnessData, "h3Indexes", [])
        .filter(({ startIndex }) => startIndex === h3Index)
        .reduce(
          (
            acc: {
              hotspotsTooCloseToWitness: Hotspot[];
              locationsTooCloseToWitness: HotspotLocation[];
            },
            { info: { endIndex } }
          ) => {
            values(locations)
              .filter((location) => location.h3_index_r12 === endIndex)
              .forEach((location) => {
                if (
                  location.uuid !== id &&
                  !acc.locationsTooCloseToWitness.includes(location)
                ) {
                  acc.locationsTooCloseToWitness.push(location);
                }
              });

            values(hotspotsMap)
              .filter(
                (hotspot) =>
                  hotspot.location === endIndex &&
                  hotspot.id !== id &&
                  !acc.hotspotsTooCloseToWitness.includes(hotspot)
              )
              .forEach((hotspot) => {
                acc.hotspotsTooCloseToWitness.push(hotspot);
              });

            return acc;
          },
          { hotspotsTooCloseToWitness: [], locationsTooCloseToWitness: [] }
        ),
    [h3Index, hotspotsMap, id, locations, tooCloseToWitnessData]
  );

  const renderDistanceSection = useCallback(
    (nearbyH3Index: string) => {
      const data = get(tooCloseToWitnessData, "h3Indexes", []).filter(
        ({ startIndex, info: { endIndex, distanceR11, distanceMeters } }) =>
          startIndex === h3Index && endIndex === nearbyH3Index
      );

      if (isEmpty(data)) return null;

      const {
        info: { distanceR11, distanceMeters }
      } = head(data) as TooCloseToWitnessData;

      return (
        <>
          <td className="px-6 py-4 whitespace-nowrap text-sm">
            {`${distanceR11} res ${chainVars?.pocV4ParentRes as number} hexes`}
          </td>
          <td className="px-6 py-4 whitespace-nowrap text-sm">
            {renderDistance(Math.round(distanceMeters), false)}
          </td>
        </>
      );
    },
    [chainVars?.pocV4ParentRes, h3Index, renderDistance, tooCloseToWitnessData]
  );

  const renderNearbyHotspot = useCallback(
    (nearbyHotspot: Hotspot) => {
      const hotspotCoordinates = getHotspotCoordinates(nearbyHotspot.id);

      if (!hotspotCoordinates) return null;

      return (
        <tr key={nearbyHotspot.id}>
          <td className="px-6 py-4 whitespace-nowrap">
            <Link
              className="relative flex items-center space-x-3 group"
              to={`/hotspots/${nearbyHotspot.id}/witnesses/witnessed-others`}
            >
              <div className="flex-shrink-0">
                <img
                  className="h-10 w-10 rounded-full"
                  src={stringifyUrl({
                    url: `${avatarsApi}/bottts/${nearbyHotspot.id}.svg`,
                    query: { w: 40, h: 40 }
                  })}
                  alt="Hotspot avatar"
                />
              </div>
              <div className="flex-1 min-w-0 focus:outline-none">
                <span className="absolute inset-0" aria-hidden="true" />
                <p className="text-sm font-medium text-gray-900 dark:text-white group-hover:underline">
                  {formatHotspotName(nearbyHotspot)}
                </p>
                <DisplayLabels
                  linkedUuid={nearbyHotspot.id}
                  size="small"
                  contextWalletAddresses={[nearbyHotspot.owner]}
                />
              </div>
            </Link>
          </td>

          {renderDistanceSection(hotspotCoordinates.h3Index)}
        </tr>
      );
    },
    [getHotspotCoordinates, renderDistanceSection]
  );

  const renderNearbyLocation = useCallback(
    (nearbyLocation: HotspotLocation) => {
      const locationCoordinates = getLocationCoordinates(nearbyLocation.uuid);

      if (!locationCoordinates) return null;

      return (
        <tr key={nearbyLocation.uuid}>
          <td className="px-6 py-4 whitespace-nowrap">
            <Link
              className="relative flex items-center space-x-3 group"
              to={`/locations/${nearbyLocation.uuid}/witnesses`}
            >
              <div className="flex-shrink-0 flex justify-center items-center w-10">
                <LocationMarkerIcon className="h-6 w-6" />
              </div>
              <div className="flex-1 min-w-0 focus:outline-none">
                <p className="text-sm font-medium text-gray-900 dark:text-white group-hover:underline">
                  {nearbyLocation.name}
                </p>
                <DisplayLabels linkedUuid={nearbyLocation.uuid} size="small" />
              </div>
            </Link>
          </td>

          {renderDistanceSection(locationCoordinates.h3Index)}
        </tr>
      );
    },
    [getLocationCoordinates, renderDistanceSection]
  );

  if (
    (isEmpty(hotspotsTooCloseToWitness) &&
      isEmpty(locationsTooCloseToWitness)) ||
    !chainVars
  ) {
    return null;
  }

  return (
    <div className={`flex flex-col ${className}`}>
      <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
        <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
          <div className="shadow overflow-hidden sm:rounded-lg">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-500">
              <thead className="bg-gray-50 text-gray-500 dark:bg-gray-700 dark:text-gray-300">
                <tr>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider"
                  >
                    <div className="flex items-center">
                      <span className="mr-1">
                        {hotspotsTooCloseToWitness.length +
                          locationsTooCloseToWitness.length}{" "}
                        too close to witness
                      </span>
                      <Tooltip
                        placement="top"
                        overlay={`Hotspots that have less than ${chainVars.pocV4ExclusionCells} res ${chainVars.pocV4ParentRes} hexes apart from each other will not be rewarded for witnessing each other.`}
                      >
                        <InfoIcon className="inline-flex h-4 w-4" />
                      </Tooltip>
                    </div>
                  </th>

                  <th
                    scope="col"
                    style={{ minWidth: "9rem" }}
                    className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider"
                  >
                    Hex distance
                  </th>

                  <th
                    scope="col"
                    style={{ minWidth: "9rem" }}
                    className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider"
                  >
                    Metric distance
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200 text-gray-500 dark:text-gray-50 dark:divide-gray-500 dark:bg-gray-800">
                {hotspotsTooCloseToWitness.map(renderNearbyHotspot)}
                {locationsTooCloseToWitness.map(renderNearbyLocation)}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NeighborsTooCloseToWitness;
